import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { GameState } from '@/game-state';
import { Button, Confirm } from '@/ui/components';
import { useClient, useLocale } from '@/ui/context';
import { capitalize } from '@/utils';
import { MAIN_WORLD, WORLD_ID, WORLD_NAME } from '@/world';
import { WorldPicker } from './world-picker';

const DISCORD_AUTH_SESSION_KEY = 'lulseo:discord-auth-session';

type DiscordAuthSession = {
  username?: string;
  token?: string;
  created?: boolean;
  discordUsername?: string;
};

export function MainMenu() {
  const client = useClient();
  const { locale } = useLocale();
  const [deletingUsername, setDeletingUsername] = useState<string | null>(null);
  const [pickingWorld, setPickingWorld] = useState(() => new URLSearchParams(location.search).get('world') !== WORLD_ID);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [busy, setBusy] = useState(client.loginPending);
  const [loginStatus, setLoginStatus] = useState(client.loginStatus);
  useEffect(() => client.subscribeLoginProgress(() => { setBusy(client.loginPending); setLoginStatus(client.loginStatus); }), [client]);

  useEffect(() => {
    return client.authenticationController.subscribeLoginFailed(() => {
      setSessionVersion((version) => version + 1);
    });
  }, [client]);

  const sorted = useMemo(
    () =>
      [...client.authenticationController.getSessions()].sort(
        (a, b) => b.lastLoginAt - a.lastLoginAt,
      ),
    [client, sessionVersion],
  );

  const viewCredits = useCallback(() => {
    window.open(client.config.creditsUrl, '_blank');
  }, [client.config.creditsUrl]);

  const goHome = useCallback(() => {
    window.location.assign('/');
  }, []);

  const openDiscord = useCallback(() => {
    window.open(
      'https://discord.gg/bHJkdVHaS2',
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  const linkDiscord = useCallback(() => {
    window.open('/api/auth/discord/start', '_blank', 'noopener,noreferrer');
  }, []);

  const discordSignIn = useCallback(() => {
    window.location.assign('/api/auth/discord/start?intent=signin');
  }, []);

  const discordSignUp = useCallback(() => {
    window.location.assign('/api/auth/discord/start?intent=signup');
  }, []);

  const createAccount = useCallback(() => {
    if (client.loginPending) return;
    if (client.state === GameState.Initial) {
      client.connect(GameState.CreateAccount);
    } else {
      client.setState(GameState.CreateAccount);
    }
  }, [client]);

  const playGame = useCallback(() => {
    if (client.loginPending) return;
    client.authenticationController.skipAutoLogin();
    if (client.state === GameState.Initial) {
      client.connect(GameState.Login);
    } else {
      client.setState(GameState.Login);
    }
  }, [client]);

  const playSession = useCallback(
    (username: string) => {
      if (client.loginPending) return;
      const ok = client.authenticationController.selectSession(username);
      if (!ok) return;
      if (client.state === GameState.Initial) {
        client.connect(GameState.Login);
      } else {
        const didAuto = client.authenticationController.autoLogin();
        if (!didAuto) {
          client.setState(GameState.Login);
        }
      }
    },
    [client],
  );

  useEffect(() => {
    if (MAIN_WORLD) return; // Discord auth currently belongs to Legacy only.
    const raw = sessionStorage.getItem(DISCORD_AUTH_SESSION_KEY);
    if (!raw) return;

    sessionStorage.removeItem(DISCORD_AUTH_SESSION_KEY);

    let session: DiscordAuthSession;
    try {
      session = JSON.parse(raw) as DiscordAuthSession;
    } catch {
      client.alertController.show(
        'Discord Sign In',
        'Discord returned an invalid sign-in session. Try again.',
      );
      return;
    }

    const username = session.username?.trim().toLowerCase();
    const token = session.token?.trim();
    if (!username || !token) {
      client.alertController.show(
        'Discord Sign In',
        'Discord did not return a usable game session. Try again.',
      );
      return;
    }

    client.authenticationController.saveSession(username, token);
    setPickingWorld(false);
    client.authenticationController.selectSession(username);
    setSessionVersion((version) => version + 1);
    client.alertController.show(
      session.created ? 'Discord Sign Up' : 'Discord Sign In',
      session.created
        ? `Created EOWorld account ${capitalize(username)} from Discord.`
        : `Signed in as ${capitalize(username)} from Discord.`,
    );

    if (client.state === GameState.Initial) {
      client.connect(GameState.Login);
    } else {
      const didAuto = client.authenticationController.autoLogin();
      if (!didAuto) {
        client.setState(GameState.Login);
      }
    }
  }, [client]);

  const requestDelete = useCallback((username: string) => {
    setDeletingUsername(username);
  }, []);

  const confirmDelete = useCallback(() => {
    if (deletingUsername) {
      client.authenticationController.removeSession(deletingUsername);
      setSessionVersion((version) => version + 1);
      setDeletingUsername(null);
    }
  }, [client, deletingUsername]);

  const cancelDelete = useCallback(() => {
    setDeletingUsername(null);
  }, []);

  const formatLastLogin = (ts: number) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    const diffHours = Math.floor(diffMs / 3_600_000);
    const diffDays = Math.floor(diffMs / 86_400_000);

    if (diffMins < 1) return locale.selectSession.lastLoginJustNow;
    if (diffMins < 60)
      return locale.selectSession.lastLoginMinutesAgo.replace(
        '{0}',
        String(diffMins),
      );
    if (diffHours < 24)
      return locale.selectSession.lastLoginHoursAgo.replace(
        '{0}',
        String(diffHours),
      );
    if (diffDays < 7)
      return locale.selectSession.lastLoginDaysAgo.replace(
        '{0}',
        String(diffDays),
      );
    return d.toLocaleDateString();
  };

  if (pickingWorld) return <WorldPicker />;

  return (
    <div class='flex h-full w-full flex-col items-center justify-center gap-4 align-center'>
      <div class='flex w-full max-w-sm flex-col gap-2 px-4'>
        <div class='flex items-center justify-between rounded-lg bg-base-100 p-3'>
          <strong>{WORLD_NAME}</strong><Button disabled={busy} variant={['ghost', 'sm']} onClick={() => setPickingWorld(true)}>Change world</Button>
        </div>
        {MAIN_WORLD && <>
          <div class='rounded-lg bg-neutral p-3 text-center text-sm leading-relaxed text-neutral-content'>
            Monument — 1× normal play, 2× events. Create a new account to begin. Discord sign-in is available on Legacy only.
          </div>
          <div role='note' class='rounded-lg border border-warning/50 bg-neutral p-3 text-center text-sm font-semibold leading-relaxed text-warning'>
            No botting on Monument. This includes AFK hitting. Players must be present while attacking.
          </div>
        </>}
        {busy && <p role='status' class='text-center text-sm'>{loginStatus}</p>}
        <div class='flex flex-col gap-2 md:hidden'>
          <Button disabled={busy} variant={['primary', 'lg', 'block']} onClick={() => sorted[0] ? playSession(sorted[0].username) : playGame()}>
            {busy ? 'Connecting…' : 'Quick login'}
          </Button>
          <p class='text-center text-sm opacity-75'>
            {sorted[0] ? `Continue as ${capitalize(sorted[0].username)}` : 'Sign in to start playing.'}
          </p>
        </div>
        {sorted.length > 0 && (
          <div class='card bg-base-100 shadow-sm'>
            <div class='card-body p-4'>
              <div class='card-title text-sm'>{locale.selectSession.title}</div>
              <div class='flex flex-col gap-1'>
                {sorted.map((session) => (
                  <div
                    key={session.username}
                    class='flex items-center justify-between gap-3 rounded-lg bg-base-200 px-3 py-1.5'
                  >
                    <div class='flex flex-col'>
                      <span class='font-medium text-sm'>
                        {capitalize(session.username)}
                      </span>
                      <span class='text-xs opacity-60'>
                        {formatLastLogin(session.lastLoginAt)}
                      </span>
                    </div>
                    <div class='flex gap-1'>
                      <Button
                        variant={['primary', 'sm']}
                        disabled={busy}
                        onClick={() => playSession(session.username)}
                      >
                        {locale.selectSession.btnPlay}
                      </Button>
                      <Button
                        variant={['ghost', 'sm']}
                        onClick={() => requestDelete(session.username)}
                      >
                        {locale.selectSession.btnDelete}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {!MAIN_WORLD && <Button variant='primary' onClick={discordSignIn}>
          Sign in with Discord
        </Button>}
        {!MAIN_WORLD && <Button onClick={discordSignUp}>Sign up with Discord</Button>}
        <Button disabled={busy} onClick={createAccount}>
          {locale.mainMenu.btnCreateAccount}
        </Button>
        <Button disabled={busy} onClick={playGame}>{locale.mainMenu.btnLogin}</Button>
        <Button onClick={goHome}>Homepage</Button>
        <Button onClick={openDiscord}>Discord</Button>
        {!MAIN_WORLD && <Button onClick={linkDiscord}>Link Discord</Button>}
        <Button onClick={viewCredits}>{locale.mainMenu.btnViewCredits}</Button>
      </div>
      {deletingUsername && (
        <Confirm
          title={locale.selectSession.confirmDeleteTitle}
          message={locale.selectSession.confirmDelete.replace(
            '{0}',
            deletingUsername,
          )}
          onYes={confirmDelete}
          onNo={cancelDelete}
        />
      )}
    </div>
  );
}
